/**
 * Connections — Integrations tile grid. All MOCKED — every tile is "Not
 * connected" except internal CRM Sync. Hooking up real providers (Ayrshare,
 * Resend, Wix MCP, G2, GA4) is a follow-up backend task.
 */
import MarketingShell from "@/components/MarketingShell";
import { Linkedin, Instagram, Twitter, Facebook, Youtube, Mail, Globe, BarChart3, Star, MessageSquare, Cloud, Plug } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  {
    id: "social", title: "Social channels", subtitle: "Unified social publishing (planned · Ayrshare)",
    items: [
      { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0077B5", status: "disconnected" },
      { id: "instagram", label: "Instagram Business", icon: Instagram, color: "#E4405F", status: "disconnected" },
      { id: "x", label: "X / Twitter", icon: Twitter, color: "#0f1419", status: "disconnected" },
      { id: "facebook", label: "Facebook Pages", icon: Facebook, color: "#1877F2", status: "disconnected" },
      { id: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000", status: "disconnected" },
      { id: "gbp", label: "Google Business Profile", icon: Globe, color: "#34A853", status: "disconnected" },
    ],
  },
  {
    id: "email", title: "Email", subtitle: "Transactional + marketing send (planned · Resend)",
    items: [
      { id: "resend", label: "Resend", icon: Mail, color: "#000000", status: "disconnected" },
      { id: "sendgrid", label: "SendGrid", icon: Mail, color: "#1A82E2", status: "disconnected" },
    ],
  },
  {
    id: "blog", title: "Blog / Web", subtitle: "Direct blog publishing (Wix — planned)",
    items: [
      { id: "wix", label: "Wix Studio (Velo)", icon: Globe, color: "#0C6EFC", status: "disconnected" },
      { id: "wordpress", label: "WordPress MCP", icon: Globe, color: "#21759B", status: "disabled" },
    ],
  },
  {
    id: "analytics", title: "Analytics", subtitle: "Cross-channel performance ingestion",
    items: [
      { id: "ga4", label: "Google Analytics 4", icon: BarChart3, color: "#F9AB00", status: "disconnected" },
      { id: "posthog", label: "PostHog", icon: BarChart3, color: "#1D4AFF", status: "disconnected" },
    ],
  },
  {
    id: "reviews", title: "Reviews", subtitle: "Buyer-intent + aggregateRating schema",
    items: [
      { id: "g2", label: "G2 / Capterra", icon: Star, color: "#FF492C", status: "disconnected" },
      { id: "trustpilot", label: "Trustpilot", icon: Star, color: "#00B67A", status: "disabled" },
    ],
  },
  {
    id: "comms", title: "Comms", subtitle: "Approval pings + agent digests",
    items: [
      { id: "slack", label: "Slack", icon: MessageSquare, color: "#4A154B", status: "disconnected" },
      { id: "teams", label: "Microsoft Teams", icon: MessageSquare, color: "#5059C9", status: "disabled" },
    ],
  },
  {
    id: "knowledge", title: "Knowledge / Assets", subtitle: "Asset library + content briefs",
    items: [
      { id: "gdrive", label: "Google Drive", icon: Cloud, color: "#4285F4", status: "disconnected" },
      { id: "notion", label: "Notion", icon: Cloud, color: "#000000", status: "disconnected" },
      { id: "figma", label: "Figma (templates)", icon: Cloud, color: "#F24E1E", status: "disconnected" },
    ],
  },
  {
    id: "internal", title: "Internal", subtitle: "PHI-firewalled de-identified CRM consumption",
    items: [
      { id: "crm-sync", label: "Enable CRM Sync", icon: Plug, color: "var(--success)", status: "connected" },
    ],
  },
];

const STATUS_TAG = { connected: "tag-green", disconnected: "tag-gray", disabled: "tag-amber" };
const STATUS_LABEL = { connected: "Connected", disconnected: "Not connected", disabled: "Coming soon" };

export default function Connections() {
  return (
    <MarketingShell title="Connections" subtitle="One place to wire up every channel · 8 categories · 22 providers">
      <div data-testid="connections-page">
        {SECTIONS.map((s) => (
          <div key={s.id} style={{ marginBottom: 28 }}>
            <div style={{ marginBottom: 12 }}>
              <h3 className="card-title">{s.title}</h3>
              <p style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginTop: 2 }}>{s.subtitle}</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              {s.items.map((it) => {
                const Icon = it.icon;
                const disabled = it.status === "disabled";
                return (
                  <div key={it.id} className="mkt-card" style={{ padding: "14px 16px", opacity: disabled ? 0.55 : 1 }} data-testid={`connection-${it.id}`}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: `${typeof it.color === "string" && it.color.startsWith("#") ? it.color : "#1B365D"}12`, color: it.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon style={{ width: 16, height: 16 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 580, color: "var(--navy)" }}>{it.label}</div>
                        <span className={`tag ${STATUS_TAG[it.status]}`} style={{ marginTop: 4, display: "inline-block" }}>
                          {STATUS_LABEL[it.status]}
                        </span>
                      </div>
                      <button
                        className={it.status === "connected" ? "btn" : "btn btn-primary"}
                        style={{ height: 28, fontSize: 11, padding: "0 10px" }}
                        disabled={disabled}
                        onClick={() => toast.info(`${it.label} OAuth · MOCKED`)}
                        data-testid={`connect-${it.id}`}
                      >
                        {it.status === "connected" ? "Manage" : disabled ? "Soon" : "Connect"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </MarketingShell>
  );
}
